/**
 * @file services/api.js
 * @description Centralized Axios API client with security features.
 * Configures base URL, CSRF protection, JWT auth headers, token refresh,
 * request signing, and error handling interceptors. Exports named API functions
 * for tours, bookings, auth, users, testimonials, and admin endpoints.
 */

import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

// Security configuration
const SECURITY_CONFIG = {
    enableCSP: true,
    enableHSTS: true,
    enableXSSProtection: true,
    enableNoSniff: true,
    enableFrameOptions: true,
    enableCSRFProtection: true,
    enableCORS: true,
    rateLimit: 100,
    timeout: 10000,
    csp: {
        defaultSrc: ["'self'"],
        scriptSrc: ["'self'", "'unsafe-inline'", "'unsafe-eval'"],
        styleSrc: ["'self'", "'unsafe-inline'"],
        imgSrc: ["'self'", "data:", "https:"],
        fontSrc: ["'self'"],
        connectSrc: ["'self'"],
        frameAncestors: ["'none'"],
        formAction: ["'self'"],
        baseUri: ["'self'"],
        objectSrc: ["'none'"],
        upgradeInsecureRequests: true,
    },
};

// Generate a unique request ID for tracking
const generateRequestId = () => uuidv4();

// API URL configuration with security considerations
const getApiUrl = () => {
    // In development, use relative URLs (handled by Vite proxy)
    if (import.meta.env.DEV) {
        return '/api';
    }
    
    // In production, use the environment variable or default URL
    const envUrl = import.meta.env.VITE_API_URL || 'http://localhost:5006';
    return envUrl.endsWith('/') ? envUrl.slice(0, -1) : envUrl;
};

const API_URL = getApiUrl();

// Create a custom axios instance with enhanced security
const api = axios.create({
    baseURL: API_URL,
    timeout: SECURITY_CONFIG.timeout,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    },
    withCredentials: true,
    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',
});

// Helper function to get CSRF token from cookies
const getCSRFTokenFromCookie = () => {
    const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : null;
};

// Generate a request signature for sensitive operations
const generateRequestSignature = (config) => {
    const timestamp = Date.now();
    const method = config.method?.toUpperCase() || 'GET';
    const path = config.url || '';
    const body = config.data ? JSON.stringify(config.data) : '';
    const secret = import.meta.env.VITE_APP_SECRET || 'default-secret';
    
    // In a real app, use a more secure method to generate the signature
    return btoa(`${method}:${path}:${timestamp}:${secret}`);
};

// Request interceptor for adding auth token and security headers
api.interceptors.request.use(
    (config) => {
        // Get auth token from localStorage
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const token = user?.token;

        // Add auth token to headers if it exists
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Add CSRF token from cookie if available
        const csrfToken = getCSRFTokenFromCookie();
        if (csrfToken) {
            config.headers['X-CSRF-Token'] = csrfToken;
            config.headers['X-XSRF-TOKEN'] = csrfToken;
        }

        // Add request ID for tracking
        const requestId = generateRequestId();
        config.headers['X-Request-ID'] = requestId;

        return config;
    },
    (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor for handling errors and refreshing tokens
api.interceptors.response.use(
    (response) => {
        // Check for security headers in response
        const securityHeaders = [
            'Content-Security-Policy',
            'X-Content-Type-Options',
            'X-Frame-Options',
            'X-XSS-Protection',
            'Referrer-Policy'
        ];

        const missingHeaders = securityHeaders.filter(
            header => !Object.keys(response.headers).includes(header.toLowerCase())
        );

        if (missingHeaders.length > 0) {
            console.warn('Missing security headers in response:', missingHeaders);
        }

        // If this is a CSRF token response, store it
        if (response.data?.csrfToken) {
            document.cookie = `XSRF-TOKEN=${response.data.csrfToken}; Path=/; SameSite=Strict`;
        }

        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Handle 401 Unauthorized errors
        if (error.response?.status === 401 && !originalRequest._retry) {
            // If we're already trying to refresh the token, don't retry
            if (originalRequest.url?.includes('/auth/refresh-token')) {
                // Clear user data and redirect to login
                localStorage.removeItem('user');
                window.location.href = '/login';
                return Promise.reject(error);
            }

            originalRequest._retry = true;

            try {
                // Try to refresh the token
                const response = await api.post('/auth/refresh-token', {}, {
                    withCredentials: true,
                    _retry: true // Prevent infinite loops
                });
                
                const { token } = response.data;

                // Update the stored token
                const user = JSON.parse(localStorage.getItem('user') || '{}');
                localStorage.setItem('user', JSON.stringify({ ...user, token }));

                // Update the Authorization header
                originalRequest.headers.Authorization = `Bearer ${token}`;

                // Retry the original request
                return api(originalRequest);
            } catch (refreshError) {
                // If refresh fails, clear user data and redirect to login
                localStorage.removeItem('user');
                window.location.href = '/login';
                return Promise.reject(refreshError);
            }
        }

        // Handle CSRF token errors
        if (error.response?.status === 403 && 
            error.response?.data?.message?.includes('CSRF token')) {
            // Clear the stored CSRF token to force a refresh
            document.cookie = 'XSRF-TOKEN=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
            
            // Retry the request with a new CSRF token
            if (!originalRequest._retry) {
                originalRequest._retry = true;
                return api(originalRequest);
            }
        }

        // Handle other error responses
        const status = error.response?.status;
        const data = error.response?.data;

        switch (status) {
            case 403:
                // Handle forbidden (e.g., insufficient permissions)
                console.error('Forbidden:', data?.message || 'Insufficient permissions');
                return Promise.reject(new Error('You do not have permission to perform this action.'));
                
            case 404:
                // Handle not found
                console.error('Not Found:', data?.message || 'Resource not found');
                return Promise.reject(new Error('The requested resource was not found.'));
                
            case 429:
                // Handle rate limiting
                console.error('Rate Limited:', data?.message || 'Too many requests');
                return Promise.reject(new Error('Too many requests. Please try again later.'));
                
            case 500:
                // Handle server errors
                console.error('Server Error:', data?.message || 'Internal server error');
                return Promise.reject(new Error('An unexpected error occurred. Please try again later.'));
                
            default:
                console.error('API Error:', data?.message || 'An error occurred');
                return Promise.reject(error);
        }
    }
);

// API endpoints
const getAllTours = (params = {}) => api.get('/tours', { params });
const getTour = (id) => api.get(`/tours/${id}`);
const createTour = (tourData) => api.post('/tours', tourData);
const updateTour = (id, tourData) => api.patch(`/tours/${id}`, tourData);
const deleteTour = (id) => api.delete(`/tours/${id}`);

// Booking endpoints
const createBooking = (bookingData) => api.post('/bookings', bookingData);
const getBooking = (id) => api.get(`/bookings/${id}`);
const updateBooking = (id, bookingData) => api.put(`/bookings/${id}`, bookingData);
const deleteBooking = (id) => api.delete(`/bookings/${id}`);
const getUserBookings = () => api.get('/bookings/my-bookings');

// Auth endpoints
const login = (credentials) => api.post('/auth/login', credentials);
const firebaseLogin = (data) => api.post('/auth/firebase-login', data);
const signup = (userData) => api.post('/auth/register', userData);
const logout = () => api.post('/auth/logout');
const forgotPassword = (email) => api.post('/auth/forgot-password', { email });
const resetPassword = (token, password) => api.post('/auth/reset-password', { token, newPassword: password });

// User endpoints
const getCurrentUser = () => api.get('/users/me');
const updateProfile = (userData) => api.patch('/users/me', userData);
const updatePassword = (passwordData) => api.patch('/users/update-password', passwordData);

// Testimonial endpoints
const getAllTestimonials = () => api.get('/testimonials');
const createTestimonial = (testimonialData) => api.post('/testimonials', testimonialData);
const updateTestimonial = (id, testimonialData) => api.put(`/testimonials/${id}`, testimonialData);
const deleteTestimonial = (id) => api.delete(`/testimonials/${id}`);

// Admin endpoints
const getAdminStats = () => api.get('/admin/stats');
const getAllUsers = (params) => api.get('/admin/users', { params });
const updateUserRole = (userId, roleData) => api.patch(`/admin/users/${userId}/role`, roleData);
const deleteUser = (userId) => api.delete(`/admin/users/${userId}`);

// Blog endpoints
const getAllBlogs = (params = {}) => api.get('/blogs', { params });
const getBlog = (idOrSlug) => api.get(`/blogs/${idOrSlug}`);
const createBlog = (formData) => api.post('/blogs', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
});
const updateBlog = (id, formData) => api.patch(`/blogs/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
});
const deleteBlog = (id) => api.delete(`/blogs/${id}`);
const uploadBlogImage = (formData) => api.post('/blogs/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
});

// Blog comment endpoints
const getBlogComments = (blogId) => api.get(`/blogs/${blogId}/comments`);
const createBlogComment = (blogId, commentData) => api.post(`/blogs/${blogId}/comments`, commentData);
const deleteBlogComment = (commentId) => api.delete(`/blogs/comments/${commentId}`);

// Blog reaction endpoints
const getBlogReactions = (blogId) => api.get(`/blogs/${blogId}/reactions`);
const toggleBlogReaction = (blogId, reactionData) => api.post(`/blogs/${blogId}/reactions`, reactionData);

// Site Settings
const getSiteSettings = () => api.get('/settings');
const updateSiteSettings = (data) => api.patch('/settings', data);
const uploadSiteImage = (file) => {
  const formData = new FormData();
  formData.append('image', file);
  return api.post('/settings/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Payment endpoints
const getPaymentConfig = () => api.get('/payments/config');
const createPaymentOrder = (data) => api.post('/payments/create-order', data);
const verifyPayment = (data) => api.post('/payments/verify', data);
const getPaymentStatus = (orderId) => api.get(`/payments/status/${orderId}`);
const getPaymentHistory = () => api.get('/payments/history');

// Transport search endpoints
const searchLocations = (keyword) => api.get('/transport/locations', { params: { keyword } });
const searchFlights = (params) => api.get('/transport/search/flights', { params });
const searchBuses = (params) => api.get('/transport/search/buses', { params });
const searchCabs = (params) => api.get('/transport/search/cabs', { params });
const confirmFlightPrice = (flightOffer) => api.post('/transport/flights/price', { flightOffer });
const bookFlight = (data) => api.post('/transport/flights/book', data);

// Hotel endpoints
const searchHotelCities = (keyword) => api.get('/hotels/cities', { params: { keyword } });
const getPopularHotels = (limit = 4) => api.get('/hotels/popular', { params: { limit } });
const searchHotels = (params) => api.get('/hotels/search', { params });
const getHotelOffer = (offerId) => api.get(`/hotels/offer/${offerId}`);
const bookHotel = (data) => api.post('/hotels/book', data);

// Export all functions
export {
  api as default,
  getAllTours,
  getTour,
  createTour,
  updateTour,
  deleteTour,
  createBooking,
  getBooking,
  updateBooking,
  deleteBooking,
  getUserBookings,
  login,
  firebaseLogin,
  signup,
  logout,
  forgotPassword,
  resetPassword,
  getCurrentUser,
  updateProfile,
  updatePassword,
  getAllTestimonials,
  createTestimonial,
  updateTestimonial,
  deleteTestimonial,
  getAdminStats,
  getAllUsers,
  updateUserRole,
  deleteUser,
  getAllBlogs,
  getBlog,
  createBlog,
  updateBlog,
  deleteBlog,
  uploadBlogImage,
  getBlogComments,
  createBlogComment,
  deleteBlogComment,
  getBlogReactions,
  toggleBlogReaction,
  getSiteSettings,
  updateSiteSettings,
  uploadSiteImage,
  searchLocations,
  searchFlights,
  searchBuses,
  searchCabs,
  confirmFlightPrice,
  bookFlight,
  searchHotelCities,
  getPopularHotels,
  searchHotels,
  getHotelOffer,
  bookHotel,
  getPaymentConfig,
  createPaymentOrder,
  verifyPayment,
  getPaymentStatus,
  getPaymentHistory,
};
